import { HttpAdapter } from 'js-data-http'
import { get } from 'lodash'
import UrlHelper from '@/util/Url'
import AuthUtil from '@/util/AuthUtil'
import store from '@/store'
import Vue from 'vue'

const VerofreshApiAdapter = new HttpAdapter({
  debug: true,
  basePath: UrlHelper.getApiUrl(),
  beforeHTTP: function (config, opts) {
    config.headers || (config.headers = {})

    let requireAuth = true
    if (Object.prototype.hasOwnProperty.call(config, 'requireAuth')) {
      requireAuth = config.requireAuth
    }

    if (requireAuth) {
      config.headers.authorization = AuthUtil.getAuthorizationHeader()
    }

    let requireCustomerContext = true
    if (Object.prototype.hasOwnProperty.call(config, 'requireCustomerContext')) {
      requireCustomerContext = config.requireCustomerContext
    }

    if (requireCustomerContext) {
      config.headers['verofresh-customer-context'] = store.getters['userModule/customerContextId']
    }

    // Add New Relic trace headers if available
    try {
      const dt = Vue.prototype.$newrelic?.features?.ajax?.dt
      const payload = dt?.generateTracePayload?.({ sameOrigin: true })

      if (payload?.traceContextParentHeader) {
        config.headers['traceparent'] = payload.traceContextParentHeader
      }
      if (payload?.traceContextStateHeader) {
        config.headers['tracestate'] = payload.traceContextStateHeader
      }
    } catch (err) {
      console.warn('[NewRelic] Failed to inject trace headers:', err)
    }

    // Now do the default behavior
    return HttpAdapter.prototype.beforeHTTP.call(this, config, opts)
  },
  afterHTTP: function (config, opts, response) {
    // If we did not get a valid response object (response code 2XX) then exit
    // early
    if (response === undefined) {
      return
    }

    const jwt = AuthUtil.parseJwt(response)
    if (jwt) {
      AuthUtil.setJwt(jwt)
      store.dispatch('userModule/setSessionExpirationTime', AuthUtil.jwtExpiration(false))
    }
  },
  responseError: function (err, config, opts) {
    // err.response is undefined whenever the request failed without ever
    // producing an HTTP response (network drop, request aborted because the
    // user navigated away, CORS block). Dereferencing it unguarded threw a
    // TypeError *inside this handler*, which replaced the real error and left
    // callers showing the generic "error while trying to process your request"
    // modal - and meant the 401 redirect below never ran. (VF-4310)
    const status = get(err, 'response.status')
    if (status === 401) {
      // Forward to login page, but reload the page to avoid any promise
      // rejections being called due to this error
      window.location.href = '/login'
      // Return a promise that sleeps for 10 seconds, should interrupt further
      // js execution for long enough for the page reload to happen.
      return new Promise(resolve => setTimeout(resolve, 10000))
    } else if (status === 503) {
      store.dispatch('globalMessagingModule/setMaintenanceMode', true)
      return Promise.reject(err)
    }
    return HttpAdapter.prototype.responseError.call(this, err, config, opts)
  },
  deserialize: function (resource, data) {
    // Shove the response headers into the first element. This allows the
    // downstream mapper to still produce the appropriate record instances
    // (e.g. UserRecord) for each result, and then we catch and pull the
    // metadata off the first record in the BaseMapper
    if (Array.isArray(data.data)) {
      if (data.data.length > 0) {
        data.data[0].responseHeaders = data.headers
      } else {
        data.data.push({ responseHeaders: data.headers })
      }
    }

    return data.data
  }
})

export default VerofreshApiAdapter
